import React, { useState } from 'react';
import '../Editor.css'; // Import the new shared editor styles

const ButtonEditor = ({ selectedNpc, dialogues, buttons, setButtons }) => {
  const [editingButton, setEditingButton] = useState(null);
  const [editedButtonText, setEditedButtonText] = useState('');
  const [expandedDialogues, setExpandedDialogues] = useState({});

  const handleButtonEdit = (npc, dialogue, button, index) => {
    setEditingButton({ npc, dialogue, button, index });
    setEditedButtonText(button);
  };

  const handleSaveButtonEdit = () => {
    if (!editingButton) return;
    
    const { npc, dialogue, button } = editingButton;
    
    // If the button text hasn't changed, just cancel editing
    if (button === editedButtonText) {
      setEditingButton(null);
      setEditedButtonText('');
      return;
    }
    
    // Update the button text in the buttons state
    setButtons(prevButtons => {
      const newButtons = { ...prevButtons };
      
      // Find the button in the array and update it
      if (newButtons[npc] && newButtons[npc][dialogue]) {
        const buttonIndex = newButtons[npc][dialogue].indexOf(button);
        if (buttonIndex !== -1) {
          // Create a new array with the updated button
          const updatedButtonArray = [...newButtons[npc][dialogue]];
          updatedButtonArray[buttonIndex] = editedButtonText;
          
          // Update the buttons object
          newButtons[npc] = {
            ...newButtons[npc],
            [dialogue]: updatedButtonArray
          };
        }
      }
      
      return newButtons;
    });
    
    // Clear editing state
    setEditingButton(null);
    setEditedButtonText('');
  };

  const handleDeleteButton = (npc, dialogue, button) => {
    if (window.confirm(`Are you sure you want to delete this button: "${button}"?`)) {
      setButtons(prevButtons => {
        const newButtons = { ...prevButtons };
        
        // Find the button in the array and remove it
        if (newButtons[npc] && newButtons[npc][dialogue]) {
          const buttonIndex = newButtons[npc][dialogue].indexOf(button);
          if (buttonIndex !== -1) {
            // Create a new array without the deleted button
            const updatedButtonArray = newButtons[npc][dialogue].filter((_, i) => i !== buttonIndex);
            
            // Update the buttons object
            newButtons[npc] = {
              ...newButtons[npc],
              [dialogue]: updatedButtonArray
            };
          }
        }
        
        return newButtons;
      });
    }
  };

  const handleCancelButtonEdit = () => {
    setEditingButton(null);
    setEditedButtonText('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleSaveButtonEdit();
    } else if (e.key === 'Escape') {
      handleCancelButtonEdit();
    }
  };

  const toggleDialogueExpand = (dialogueIndex) => {
    setExpandedDialogues(prev => ({
      ...prev,
      [dialogueIndex]: !prev[dialogueIndex]
    }));
  };

  return (
    <div className="editor-container button-editor-container">
      <h3>Button Editor</h3>
      {selectedNpc ? (
        dialogues[selectedNpc] && dialogues[selectedNpc].length > 0 ? (
          <div className="button-editor-content">
            {dialogues[selectedNpc].map((dialogue, dialogueIndex) => {
              if (!dialogue) return null; // Skip empty dialogues
              
              const buttonArray = buttons[selectedNpc] && buttons[selectedNpc][dialogue] 
                ? buttons[selectedNpc][dialogue] 
                : ['$placeholder_button'];
              
              // Skip dialogues with only placeholder buttons
              if (buttonArray.length === 1 && buttonArray[0] === '$placeholder_button') {
                return null;
              }
              
              const isExpanded = expandedDialogues[dialogueIndex];
              
              return (
                <div key={dialogueIndex} className="dialogue-section">
                  <div 
                    className="dialogue-header"
                    onClick={() => toggleDialogueExpand(dialogueIndex)}
                  >
                    <span className="dialogue-title">
                      <span className="dialogue-index">{dialogueIndex}:</span> 
                      {dialogue.length > 30 ? `${dialogue.substring(0, 30)}...` : dialogue}
                    </span>
                    <span className="toggle-icon">{isExpanded ? '▼' : '▶'}</span>
                  </div>
                  
                  {isExpanded && (
                    <ul className="editor-list">
                      {buttonArray.map((button, buttonIndex) => {
                        if (button === '$placeholder_button' || !button) return null;                        
                        // Check if this button is being edited
                        const isEditing = editingButton && 
                          editingButton.npc === selectedNpc && 
                          editingButton.dialogue === dialogue && 
                          editingButton.button === button;
                        
                        return (
                          <li key={`${dialogueIndex}-${buttonIndex}`} className="editor-list-item">
                            {isEditing ? (
                              <div className="button-edit-form">
                                <input
                                  type="text"
                                  value={editedButtonText}
                                  onChange={(e) => setEditedButtonText(e.target.value)}
                                  onKeyDown={handleKeyDown}
                                  className="editor-input"
                                  autoFocus
                                />
                                <div className="edit-actions">
                                  <button 
                                    className="editor-button"
                                    onClick={handleSaveButtonEdit}
                                  >
                                    Save
                                  </button>
                                  <button 
                                    className="editor-button cancel"
                                    onClick={handleCancelButtonEdit}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div className="editor-list-item-content">
                                <div className="editor-text">
                                  {button}
                                </div>
                                <div className="editor-actions">
                                  <button 
                                    className="editor-button edit"
                                    onClick={() => handleButtonEdit(selectedNpc, dialogue, button, buttonIndex)}
                                  >
                                    Edit
                                  </button>
                                  <button 
                                    className="editor-button delete"
                                    onClick={() => handleDeleteButton(selectedNpc, dialogue, button)}
                                  >
                                    Delete
                                  </button>
                                </div>
                              </div>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="empty-state">No dialogues found for this NPC. Add some dialogues using the input field above.</div>
        )
      ) : (
        <div className="empty-state">Select an NPC to view and edit buttons.</div>
      )}
    </div>
  );
};

export default ButtonEditor;
